<script setup lang="ts">
import { calculatePercentage, formatDate, formatDateFrench } from '@/utils';
import { computed, inject, onMounted, provide, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import CollectivitesManagementValidationModal from './CollectivitesManagementValidationModal.vue';
import { useCollectiviteManagementStore } from '@/stores/collectivite-management.store';
import axios from 'axios';

const route = useRoute();
const collectiviteManagementStore = useCollectiviteManagementStore();
const BACKEND_URL = inject('BACKEND_URL');
const id = route.params.id;
const menuItems = ref([] as any[]);
const activeMenuItemName = ref('');
const collectivite = ref<any>(route.meta.collectivite || null);
const collectivitesManagementValidationModalRef = ref<InstanceType<typeof CollectivitesManagementValidationModal> | null>(null);
provide('collectivite', collectivite);

onMounted(() => {
    menuItems.value = [
        { url: `/app/collectivites/manage/${id}/infos`, name: 'collectivites-manage-infos', title: 'Informations' },
        { url: `/app/collectivites/manage/${id}/gerant`, name: 'collectivites-manage-gerant', title: 'Gérant' },
        { url: `/app/collectivites/manage/${id}/prospects`, name: 'collectivites-manage-prospects', title: 'Prospects' },
        { url: `/app/collectivites/manage/${id}/membres`, name: 'collectivites-manage-membres', title: 'Membres' },
        { url: `/app/collectivites/manage/${id}/profil-financier`, name: 'collectivites-manage-profil-financier', title: 'Profil financier' },
        { url: `/app/collectivites/manage/${id}/cartes`, name: 'collectivites-manage-cartes', title: 'Cartes' },
        { url: `/app/collectivites/manage/${id}/invitations`, name: 'collectivites-manage-invitations', title: 'Invitations' },
    ];
    if (route.name) { listenRouteChanges(route.name.toString()); }
});

watch(() => route.name, (newRouteName) => {
    if (newRouteName) { listenRouteChanges(newRouteName.toString()); }
});

watch(() => collectiviteManagementStore.reloadVersion, () => {
    fetchCollectivite();
});

async function fetchCollectivite() {
    const res = await axios.get(`/backoffice/collectivites/get/${collectivite.value.id}`);
    collectivite.value = res.data;
}

function listenRouteChanges(newRouteName: string) {
    activeMenuItemName.value = newRouteName.toString();
}

const stats = computed(() => collectivite.value.stats || {});

const membersPercentage = computed(() => {
    const maxMembers = stats.value.total_users_count || 0; // Valeur par défaut pour la barre de progression
    if (maxMembers === 0) { return 0; }

    return Math.min(100, calculatePercentage(stats.value.members_count, maxMembers));
});

async function validateCollectivite() {
    const res = await collectivitesManagementValidationModalRef.value?.open(collectivite.value.id);
    if (res) {
        collectiviteManagementStore.notifyReload();
    }
}

</script>
<template>
    <section class="container-full-1 bg-grey-50">
        <div class="page-space-40"></div>
        <h4 class="s7 font-semibold px-3 pb-5">Collectivité <span class="text-sm text-grey-500"></span></h4>
        <div class="s13 flex-right gap-3 mt--2 mb-5 px-5">
            <template v-if="collectivite">
                <router-link :to="item.url" v-for="(item, index) of menuItems" :key="index"><span class="btn lg shadow-200" :class="{'white2 border-none': item.name == activeMenuItemName, 'light2': item.name != activeMenuItemName}">{{ item.title }}</span></router-link>
            </template>
            <template v-else>
                <span class="btn lg shadow-200 white2 border-none">Enregistrement de collectivité</span>
            </template>
        </div>

        <!--Collectivité info - Component-->
        <div class="s20 widget flex align-items-center px-1 py-5" v-if="collectivite">
            <div class="s2_mobile h-100p px-7">
                <div class="rounded-100 overflow-hidden item active"><!--active-->
                    <img :src="collectivite.logo ? (BACKEND_URL + '/assets/collectivites/' + collectivite.logo) : '/assets/logos/logo2.svg'" width="100%" />
                </div>
            </div>
            <div class="s3_mobile h-100p px-2">
                <h4 class="mb-2">{{ collectivite.nom || '' }}</h4>
                <h3>{{ collectivite.rccm || 'N/A' }}</h3>
            </div>
            <div class="s3_mobile h-100p px-2">
                <h4 class="font-normal mb-5">{{ (stats.members_count || 0) + ' / ' + (stats.total_users_count || 0) }} Membres</h4>
                <span class="tag-state warning stroke">En cours</span>
                <span class="tag-state notif stroke">Actif</span>
            </div>
            <div class="s4_mobile h-100p px-3">
                <h4 class="font-normal">
                    Date de création <br class="mb-2"/>
                    <b class="text-2xl">{{ formatDateFrench(collectivite.created_at) }}</b>
                </h4>
            </div>
            <div class="s5_mobile h-100p px-5" :class="{'s5_mobile': !collectivite.verrouille, 's8_mobile': collectivite.verrouille}">
                <!--Barre de progression - Component-->
                <div class="h-60 rounded-100 position-relative" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
                    <div class="widget-floating py-0 text-xl flex-right">Membres {{ stats.members_count ? Math.round((stats.members_count / stats.total_users_count) * 100) : 0 }}%</div>
                    <div class="h-100p flex align-items-center justify-content-left rounded-100 text-2xl px-5 bg-pop-green" :style="{ width: membersPercentage + '%' }">
                        {{ (stats.members_count || 0) + ' / ' + (stats.total_users_count || 0) }}
                    </div>
                </div>
                <!--End Barre de progression-->
            </div>
            <div class="s3_mobile h-100p px-3" v-if="!collectivite.verrouille">
                <custom-button type="button" :classesList="'btn-ultra w-100 lg'" @click="validateCollectivite">
                    <img src="/icons/icon-valider.svg" class="btn-icon" alt="" />
                    <span>Valider le dossier</span>
                </custom-button>
            </div>
        </div>
        <div class="s20 widget flex align-items-center px-1 py-5" v-else>
            <div class="s2_mobile h-100p px-7">
                <div class="rounded-100 overflow-hidden item active"><!--active-->
                    <img :src="'/assets/logos/logo2.svg'" width="100%" />
                </div>
            </div>
            <div class="s18_mobile h-100p px-2">
                <h4 class="mb-2">Nouvelle collectivité</h4>
                <h5>Saisir les informations de la collectivité</h5>
            </div>
        </div>
        <!--End Collectivité info-->

        <div class="clear-both h-20"></div>
        <router-view />

        <collectivites-management-validation-modal ref="collectivitesManagementValidationModalRef" />
    </section>
</template>